// The cursor belongs to the loaded editor, not the query cache: a background
// refetch must never authorize old form values against a newer source version.
export class SavedTaxReturnConflictError extends Error {
  constructor() {
    super("This saved return changed. Reload the return before saving again.");
  }
}

export class SavedTaxReturnEditor {
  blocked = false;
  private chain: Promise<unknown> = Promise.resolve();

  constructor(
    readonly id: string,
    public sourceVersion: string,
    public snapshotJson: string,
  ) {}

  save(
    snapshot: string | ((previous: string) => string),
    keepalive = false,
    request: typeof fetch = fetch,
  ): Promise<any> {
    const pending = this.chain.then(async () => {
      if (this.blocked) throw new SavedTaxReturnConflictError();
      const snapshotJson = typeof snapshot === "function" ? snapshot(this.snapshotJson) : snapshot;
      const response = await request(`/api/saved-tax-returns/${this.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        keepalive,
        body: JSON.stringify({ snapshotJson, expectedSourceVersion: this.sourceVersion }),
      });
      if (response.status === 409) {
        this.blocked = true;
        throw new SavedTaxReturnConflictError();
      }
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || `Save failed (${response.status})`);
      }
      const updated = await response.json();
      this.sourceVersion = updated.sourceVersion;
      this.snapshotJson = updated.snapshotJson;
      return updated;
    });
    // An uncertain network failure must not be retried with an invented version.
    this.chain = pending.catch(() => { this.blocked = true; });
    return pending;
  }
}

export type LoadedSavedTaxReturn = {
  id: string;
  name: string;
  editor: SavedTaxReturnEditor;
};

export function openSavedTaxReturn(row: {
  id: string; name: string; sourceVersion: string; snapshotJson: string;
}): LoadedSavedTaxReturn {
  return { id: row.id, name: row.name, editor: new SavedTaxReturnEditor(row.id, row.sourceVersion, row.snapshotJson) };
}
